import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  addDays,
  businessDate,
  fromJalali,
  subtractDays,
  systemClock,
  toJalali,
} from '@sakhtar/calendar';
import {
  V2Card,
  V2CardHeader,
  V2FormGrid,
  V2FormField,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  Icon,
  ConfirmDialog,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type {
  FiscalPeriod,
  FiscalPeriodListResponse,
  FiscalYearListResponse,
} from '../../lib/types/finance.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { faDigits } from '../../lib/faDigits.js';
import { PeriodCloseChecklist } from './PeriodCloseChecklist.js';
import { PeriodYearCard } from './PeriodYearCard.js';
import { AccountingSetupCard } from './AccountingSetupCard.js';

/** The Jalali year that follows the latest defined one (or the current one), Farvardin 1 → Esfand end. */
function suggestNextYear(latestEnd: string | null): {
  code: string;
  name: string;
  startDate: string;
  endDate: string;
} {
  const start = latestEnd
    ? addDays(businessDate(latestEnd), 1)
    : fromJalali({ jy: toJalali(systemClock.today()).jy, jm: 1, jd: 1 });
  const j = toJalali(start);
  const end = subtractDays(fromJalali({ jy: j.jy + 1, jm: j.jm, jd: j.jd }), 1);
  return { code: String(j.jy), name: `سال مالی ${j.jy}`, startDate: start, endDate: end };
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html, frame `/t/kimia/finance` →
 *   «سال مالی ۱۴۰۴» card (12 monthly period tiles + closing note) beside the
 *   «چک‌لیست بستن <ماه>» card (docs/design-system/UI_REFERENCE.md "Finance
 *   language"); Sakhtar Finance.dc.html §09 for the close/reopen confirmation.
 * canonical pattern: section cards + tile grid (PeriodYearCard.tsx) and
 *   checklist card (PeriodCloseChecklist.tsx), then the full period table
 * desktop reference: v2 finance frame (above)
 * mobile reference: none in the v2 frame — the two cards wrap
 *   (`flex-wrap`, 520px/320px bases); the <900px shell is untouched
 * light/dark reference: tokens only (`--sk-mod-*`, `--sk-*`)
 * state reference: ready · loading · empty · permission_denied ·
 *   network_error · period open / closed / current
 * status: PARTIAL_REAL
 *   - Tiles = real periods (`GET .../accounting/fiscal-periods`), grouped by
 *     the real fiscal years (`GET .../finance/fiscal-years`); the year tabs
 *     only appear with more than one year.
 *   - KIT_CONFORMANT (checklist): the reference shows five close checks; the
 *     backend evaluates only "no submitted/approved journals left in the
 *     period" and exposes no readiness API, so only that check (+ the
 *     period's own open/closed state) is shown — see PeriodCloseChecklist.tsx.
 *     No invented items.
 *   - KIT_CONFORMANT: the board's close-confirmation names how many
 *     provisional documents block the close; the close call itself still
 *     enforces the real rule server-side regardless.
 *   - The full period table (with the «سال مالی» column and the close /
 *     reopen actions), «سال مالی جدید» (`POST .../finance/fiscal-years`)
 *     and «پیشنهاد سال بعد» are kept unchanged below the reference cards.
 *
 * Reopen is `sensitive: true` in the accounting permission registry and
 * the backend requires a reason + proof id — surfaced as a real prompt,
 * not a bare confirm, so the audit trail the backend records is honest.
 */
export function FiscalPeriodsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [closeTarget, setCloseTarget] = useState<FiscalPeriod | null>(null);
  const [reopenTarget, setReopenTarget] = useState<FiscalPeriod | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  const periods = useApiGet<FiscalPeriodListResponse>(
    slug ? `${base}/api/v1/accounting/fiscal-periods` : null,
    [slug, refreshKey],
  );
  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug, refreshKey],
  );
  const [fyCode, setFyCode] = useState('');
  const [fyName, setFyName] = useState('');
  const [fyStart, setFyStart] = useState('');
  const [fyEnd, setFyEnd] = useState('');
  const [fyBusy, setFyBusy] = useState(false);
  const [fyError, setFyError] = useState<string | null>(null);
  const yearNameById = new Map(
    years.status === 'ready' ? years.data.items.map((y) => [y.fiscalYearId, y.name]) : [],
  );

  function onSuggest() {
    const latest =
      years.status === 'ready' && years.data.items.length > 0
        ? years.data.items[years.data.items.length - 1]!.endDate
        : null;
    const next = suggestNextYear(latest);
    setFyCode(next.code);
    setFyName(next.name);
    setFyStart(next.startDate);
    setFyEnd(next.endDate);
  }

  // The «سال مالی جدید» form opens already filled with the next year (code, name, dates);
  // «پیشنهاد» only refreshes it. After a save the list reloads and the next year is proposed.
  useEffect(() => {
    if (years.status === 'ready' && !fyCode && !fyName && !fyStart && !fyEnd) onSuggest();
  }, [years]);

  async function onCreateYear(event: FormEvent) {
    event.preventDefault();
    setFyError(null);
    if (!fyCode.trim() || !fyName.trim() || !fyStart || !fyEnd) {
      setFyError('کد، عنوان، تاریخ شروع و پایان سال مالی الزامی است.');
      return;
    }
    if (fyEnd <= fyStart) {
      setFyError('تاریخ پایان باید بعد از تاریخ شروع باشد.');
      return;
    }
    setFyBusy(true);
    try {
      await apiPost(`${base}/api/v1/finance/fiscal-years`, {
        code: fyCode.trim(),
        name: fyName.trim(),
        startDate: fyStart,
        endDate: fyEnd,
      });
      setFyCode('');
      setFyName('');
      setFyStart('');
      setFyEnd('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setFyError(
        bannerMessageFrom(err, 'تعریف سال مالی ناموفق بود — هم‌پوشانی با سال دیگر یا کد تکراری.'),
      );
    } finally {
      setFyBusy(false);
    }
  }

  const [pickedYearId, setPickedYearId] = useState<string | null>(null);
  const [pickedPeriodId, setPickedPeriodId] = useState<string | null>(null);

  const todayLike = (p: FiscalPeriod) => {
    const now = systemClock.today();
    return p.startDate <= now && now <= p.endDate;
  };

  const view = (() => {
    if (periods.status !== 'ready') return null;
    const all = [...periods.data.items].sort((a, b) => a.startDate.localeCompare(b.startDate));
    const yearIds: string[] = [];
    for (const p of all) if (!yearIds.includes(p.fiscalYearId)) yearIds.push(p.fiscalYearId);
    const yearList = yearIds.map((id) => ({
      fiscalYearId: id,
      name: yearNameById.get(id) ?? 'سال مالی',
    }));
    const currentPeriod = all.find((p) => todayLike(p));
    const yearId =
      pickedYearId && yearIds.includes(pickedYearId)
        ? pickedYearId
        : (currentPeriod?.fiscalYearId ?? yearIds[yearIds.length - 1] ?? null);
    const inYear = all.filter((p) => p.fiscalYearId === yearId);
    const selected =
      inYear.find((p) => p.fiscalPeriodId === pickedPeriodId) ??
      inYear.find((p) => todayLike(p)) ??
      inYear.find((p) => p.postingState === 'open') ??
      inYear[0] ??
      null;
    return { yearList, yearId, inYear, selected };
  })();

  if (!slug) return null;

  async function onConfirmClose() {
    if (!closeTarget) return;
    setBusyId(closeTarget.fiscalPeriodId);
    setError(null);
    try {
      await apiPost(
        `${base}/api/v1/accounting/fiscal-periods/${closeTarget.fiscalPeriodId}/close`,
        undefined,
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setRefreshKey((k) => k + 1);
    } catch {
      setError('بستن دوره ناموفق بود — ممکن است سند در انتظار بررسی وجود داشته باشد.');
    } finally {
      setBusyId(null);
      setCloseTarget(null);
    }
  }

  async function onConfirmReopen() {
    if (!reopenTarget) return;
    setBusyId(reopenTarget.fiscalPeriodId);
    setError(null);
    try {
      await apiPost(
        `${base}/api/v1/accounting/fiscal-periods/${reopenTarget.fiscalPeriodId}/reopen`,
        { reason: reopenReason.trim() || 'بازگشایی توسط کاربر', proofId: crypto.randomUUID() },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setRefreshKey((k) => k + 1);
    } catch {
      setError('بازگشایی دوره ناموفق بود.');
    } finally {
      setBusyId(null);
      setReopenTarget(null);
      setReopenReason('');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {periods.status === 'loading' ? <LoadingState /> : null}
      {periods.status === 'error' ? (
        <StatePanel
          variant={periods.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={periods.error.status === 403 ? 'دسترسی ندارید' : 'فهرست قابل نمایش نیست'}
        />
      ) : null}

      <AccountingSetupCard base={base} />

      {view ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: '1.7 1 520px', minWidth: 0 }}>
            <PeriodYearCard
              years={view.yearList}
              yearId={view.yearId}
              onYearChange={(id) => {
                setPickedYearId(id);
                setPickedPeriodId(null);
              }}
              periods={view.inYear}
              selectedPeriodId={view.selected?.fiscalPeriodId ?? null}
              onSelectPeriod={setPickedPeriodId}
              isCurrent={todayLike}
            />
          </div>
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            <PeriodCloseChecklist
              base={base}
              period={view.selected}
              refreshKey={refreshKey}
              busy={view.selected !== null && busyId === view.selected.fiscalPeriodId}
              onClose={setCloseTarget}
              onReopen={setReopenTarget}
            />
          </div>
        </div>
      ) : null}

      {periods.status === 'ready' ? (
        <V2DataGrid
          icon="calendar"
          title="دوره‌های مالی"
          subtitle={`${faDigits(periods.data.items.length)} دوره`}
          densitySwitch={false}
          pageSize={0}
          columns={[
            {
              key: 'name',
              header: 'دوره',
              width: 150,
              render: (p: FiscalPeriod) => (
                <span style={{ fontWeight: todayLike(p) ? 700 : 500 }}>{p.name}</span>
              ),
            },
            {
              key: 'year',
              header: 'سال مالی',
              width: 120,
              muted: true,
              render: (p: FiscalPeriod) => yearNameById.get(p.fiscalYearId) ?? '—',
            },
            {
              key: 'range',
              header: 'بازهٔ تاریخ',
              width: 200,
              render: (p: FiscalPeriod) => (
                <span className="sk-num">
                  {displayJalaliDate(p.startDate)} — {displayJalaliDate(p.endDate)}
                </span>
              ),
            },
            {
              key: 'state',
              header: 'وضعیت',
              width: 170,
              wrap: true,
              render: (p: FiscalPeriod) => (
                <>
                  <V2Badge tone={p.postingState === 'open' ? 'success' : 'neutral'}>
                    {p.postingState === 'open'
                      ? todayLike(p)
                        ? 'باز · دورهٔ جاری'
                        : 'باز'
                      : 'بسته'}
                  </V2Badge>
                  {p.closedAt ? (
                    <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginTop: 4 }}>
                      بسته‌شده در {displayJalaliDate(p.closedAt.slice(0, 10))}
                    </div>
                  ) : null}
                </>
              ),
            },
            {
              key: 'actions',
              header: '',
              width: 170,
              render: (p: FiscalPeriod) =>
                p.postingState === 'open' ? (
                  <V2Button variant="danger" size="sm" onClick={() => setCloseTarget(p)}>
                    {busyId === p.fiscalPeriodId ? 'در حال بستن…' : 'بستن دوره'}
                  </V2Button>
                ) : (
                  <V2Button variant="secondary" size="sm" onClick={() => setReopenTarget(p)}>
                    {busyId === p.fiscalPeriodId ? 'در حال بازگشایی…' : 'بازگشایی'}
                  </V2Button>
                ),
            },
          ]}
          rows={periods.data.items}
          rowKey={(p) => p.fiscalPeriodId}
          selectedKey={periods.data.items.find((p) => todayLike(p))?.fiscalPeriodId ?? null}
          rowTone={(p) => (p.postingState === 'open' ? 'success' : undefined)}
          legend={[{ tone: 'success', label: 'دورهٔ باز' }]}
          emptyState={<V2EmptyState icon="calendar" title="دوره مالی ثبت نشده" />}
        />
      ) : null}

      {error ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '13px 16px',
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-danger-soft)',
            border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
          }}
        >
          <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
            <Icon name="x" size="sm" />
          </span>
          <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{error}</span>
        </div>
      ) : null}

      <V2Card>
        <V2CardHeader
          icon="plus"
          title="سال مالی جدید"
          subtitle="شروع و پایان قابل تنظیم است؛ دوره‌های ماهانه (شمسی) خودکار ساخته می‌شوند"
          actions={
            <V2Button type="button" variant="secondary" size="sm" onClick={onSuggest}>
              پیشنهاد سال بعد
            </V2Button>
          }
        />
        <form
          noValidate
          onSubmit={onCreateYear}
          style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <V2FormGrid>
            <V2FormField label="کد" required width="short">
              <input
                className="sk-input ltr"
                value={fyCode}
                maxLength={20}
                onChange={(e) => setFyCode(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="عنوان" required width="wide">
              <input
                className="sk-input"
                value={fyName}
                placeholder="سال مالی ۱۴۰۵"
                onChange={(e) => setFyName(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="تاریخ شروع" required width="medium">
              <JalaliDateInput value={fyStart} onChange={setFyStart} />
            </V2FormField>
            <V2FormField label="تاریخ پایان" required width="medium" hint="حداکثر ۱۸ ماه">
              <JalaliDateInput value={fyEnd} onChange={setFyEnd} />
            </V2FormField>
          </V2FormGrid>
          {fyError ? (
            <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{fyError}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <V2Button type="submit" variant="primary" icon="plus" disabled={fyBusy}>
              {fyBusy ? 'در حال ثبت…' : 'تعریف سال مالی'}
            </V2Button>
          </div>
        </form>
        {years.status === 'ready' && years.data.items.length > 0 ? (
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th>کد</th>
                  <th>عنوان</th>
                  <th>شروع</th>
                  <th>پایان</th>
                  <th>تعداد دوره</th>
                </tr>
              </thead>
              <tbody>
                {years.data.items.map((y) => (
                  <tr key={y.fiscalYearId}>
                    <td className="ltr sk-num">{y.code}</td>
                    <td>{y.name}</td>
                    <td className="sk-num">{displayJalaliDate(y.startDate)}</td>
                    <td className="sk-num">{displayJalaliDate(y.endDate)}</td>
                    <td className="sk-num">{y.periodCount.toLocaleString('fa-IR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </V2Card>

      {closeTarget ? (
        <ConfirmDialog
          open
          title={`بستن دورهٔ ${closeTarget.name}؟`}
          message="پس از بستن، ثبت هیچ سند تازه‌ای در این دوره امکان‌پذیر نیست و اصلاح فقط با سند برگشت در دورهٔ باز انجام می‌شود."
          danger
          confirmLabel="بستن دوره"
          cancelLabel="انصراف"
          onConfirm={onConfirmClose}
          onCancel={() => setCloseTarget(null)}
        />
      ) : null}

      {reopenTarget ? (
        <ConfirmDialog
          open
          title={`بازگشایی دورهٔ ${reopenTarget.name}`}
          message={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span>
                این عملیات حساس است و در رویداد حسابرسی ثبت می‌شود. دلیل بازگشایی را بنویسید.
              </span>
              <input
                type="text"
                className="sk-input"
                placeholder="دلیل بازگشایی"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
              />
            </div>
          }
          danger
          confirmLabel="بازگشایی"
          cancelLabel="انصراف"
          onConfirm={onConfirmReopen}
          onCancel={() => {
            setReopenTarget(null);
            setReopenReason('');
          }}
        />
      ) : null}
    </div>
  );
}
